import { jsx as y } from "react/jsx-runtime";
import { createContext as W, useState as C, useEffect as k, useMemo as b, useContext as F, useCallback as V } from "react";
import { theme as G, ConfigProvider as z, App as $ } from "antd";
import { brand as t, gray as o, border as S, surface as B, text as v, shadows as I, overlay as j, semantic as r, brandAlpha as x } from "@digiwedge/ui-tokens/colors";
import { radii as a, spacing as c } from "@digiwedge/ui-tokens/spacing";
import { fontSize as g } from "@digiwedge/ui-tokens/typography";
import { duration as D } from "@digiwedge/ui-tokens/motion";
import K from "antd/locale/en_US.js";
import Y from "antd/locale/fr_FR.js";
const s = {
  token: {
    // =========================================================================
    // Brand Colors
    // =========================================================================
    colorPrimary: t.primary,
    colorPrimaryBg: t.primaryLight,
    colorLink: t.secondary,
    colorLinkHover: t.secondary,
    colorLinkActive: t.secondary,
    // =========================================================================
    // Semantic Colors
    // =========================================================================
    colorInfo: r.infoText,
    colorInfoBg: r.infoLight,
    colorInfoText: r.infoText,
    colorInfoTextHover: r.infoText,
    colorInfoTextActive: r.infoText,
    colorSuccess: r.successText,
    colorSuccessBg: r.successLight,
    colorSuccessText: r.successText,
    colorSuccessTextHover: r.successText,
    colorSuccessTextActive: r.successText,
    colorWarning: r.warningText,
    colorWarningBg: r.warningLight,
    colorWarningText: r.warningText,
    colorWarningTextHover: r.warningText,
    colorWarningTextActive: r.warningText,
    colorError: r.errorText,
    colorErrorBg: r.errorLight,
    colorErrorText: r.errorText,
    colorErrorTextHover: r.errorText,
    colorErrorTextActive: r.errorText,
    // =========================================================================
    // Surface & Background Colors
    // =========================================================================
    colorBgContainer: B.surface,
    colorBgElevated: B.surface,
    colorBgLayout: B.background,
    colorBgSpotlight: o[900],
    colorBgMask: j.modal,
    // =========================================================================
    // Text Colors
    // =========================================================================
    colorText: v.primary,
    colorTextSecondary: v.secondary,
    colorTextTertiary: v.secondary,
    colorTextQuaternary: v.secondary,
    // =========================================================================
    // Border Colors
    // =========================================================================
    colorBorder: S.outline,
    colorBorderSecondary: S.border,
    colorSplit: S.divider,
    // =========================================================================
    // Fill Colors (for backgrounds of interactive elements)
    // =========================================================================
    colorFill: o[100],
    colorFillSecondary: o[50],
    colorFillTertiary: o[200],
    colorFillQuaternary: o[100],
    // =========================================================================
    // Typography
    // =========================================================================
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: g.md,
    // 14px - base size
    fontSizeHeading1: g["5xl"],
    // 36px
    fontSizeHeading2: g["4xl"],
    // 30px
    fontSizeHeading3: g["3xl"],
    // 24px
    fontSizeHeading4: g["2xl"],
    // 20px
    fontSizeHeading5: g.xl,
    // 18px
    fontSizeSM: g.sm,
    // 12px
    fontSizeLG: g.lg,
    // 16px
    fontSizeXL: g.xl,
    // 18px
    lineHeight: 1.5,
    lineHeightHeading1: 1.22,
    // Tighter for large headings
    lineHeightHeading2: 1.27,
    lineHeightHeading3: 1.33,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.44,
    lineHeightLG: 1.5,
    lineHeightSM: 1.5,
    // =========================================================================
    // Spacing & Sizing
    // =========================================================================
    padding: c.md,
    // 12px
    paddingXS: c.xs,
    // 4px
    paddingSM: c.sm,
    // 8px
    paddingLG: c.lg,
    // 16px
    paddingXL: c.xl,
    // 24px
    margin: c.lg,
    // 16px
    marginXS: c.xs,
    // 4px
    marginSM: c.sm,
    // 8px
    marginLG: c.xl,
    // 24px
    marginXL: c.xxl,
    // 32px
    controlHeight: 36,
    // Base control height
    controlHeightSM: 28,
    controlHeightLG: 44,
    // Meets touch target minimum
    controlHeightXS: 24,
    // =========================================================================
    // Border Radius
    // =========================================================================
    borderRadius: a.md,
    // 8px - default
    borderRadiusSM: a.sm,
    // 4px
    borderRadiusLG: a.lg,
    // 12px
    borderRadiusXS: 2,
    // =========================================================================
    // Motion & Animation
    // =========================================================================
    motionDurationFast: `${D.fast / 1e3}s`,
    // 0.15s
    motionDurationMid: `${D.normal / 1e3}s`,
    // 0.25s
    motionDurationSlow: `${D.slow / 1e3}s`,
    // 0.35s
    motionEaseInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    motionEaseOut: "cubic-bezier(0, 0, 0.2, 1)",
    motionEaseOutBack: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    // Spring-like overshoot
    // =========================================================================
    // Shadows
    // =========================================================================
    boxShadow: I.lightPrimary,
    boxShadowSecondary: I.lightSecondary,
    // =========================================================================
    // Z-Index Scale
    // =========================================================================
    zIndexBase: 0,
    zIndexPopupBase: 1e3,
    // =========================================================================
    // Wire Frame Mode (disabled)
    // =========================================================================
    wireframe: !1
  },
  components: {
    // =========================================================================
    // Button Customizations
    // =========================================================================
    Button: {
      colorPrimary: t.primary,
      primaryColor: v.primary,
      dangerColor: t.onPrimary,
      colorPrimaryHover: t.primaryHover,
      colorPrimaryActive: t.primaryActive,
      borderRadius: a.md,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44,
      paddingInline: c.lg,
      paddingInlineSM: c.md,
      paddingInlineLG: c.xl
    },
    // =========================================================================
    // Input Customizations
    // =========================================================================
    Input: {
      colorBorder: S.outline,
      colorBgContainer: B.surface,
      borderRadius: a.md,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44,
      paddingInline: c.md
    },
    // =========================================================================
    // Select Customizations
    // =========================================================================
    Select: {
      colorBorder: S.outline,
      borderRadius: a.md,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 44
    },
    // =========================================================================
    // Card Customizations
    // =========================================================================
    Card: {
      colorBgContainer: B.surface,
      borderRadiusLG: a.lg,
      paddingLG: c.xl
    },
    // =========================================================================
    // Table Customizations
    // =========================================================================
    Table: {
      colorBgContainer: B.surface,
      borderColor: S.border,
      headerBg: o[50],
      rowHoverBg: t.primaryLight
    },
    // =========================================================================
    // Menu Customizations
    // =========================================================================
    Menu: {
      itemSelectedBg: t.primaryLight,
      itemSelectedColor: t.primary,
      borderRadius: a.md
    },
    // =========================================================================
    // Modal Customizations
    // =========================================================================
    Modal: {
      borderRadiusLG: a.lg
    },
    // =========================================================================
    // Alert Customizations
    // =========================================================================
    Alert: {
      borderRadiusLG: a.md
    },
    // =========================================================================
    // Tag Customizations
    // =========================================================================
    Tag: {
      borderRadiusSM: a.sm
    },
    // =========================================================================
    // Tooltip Customizations
    // =========================================================================
    Tooltip: {
      borderRadius: a.sm
    }
  }
}, N = {
  algorithm: G.darkAlgorithm,
  token: {
    // Inherit all light theme tokens
    ...s.token,
    // =========================================================================
    // Dark Mode Surface Overrides
    // =========================================================================
    colorBgContainer: o[800],
    colorBgElevated: o[700],
    colorBgLayout: o[900],
    colorBgSpotlight: o[700],
    // =========================================================================
    // Dark Mode Text Overrides
    // =========================================================================
    colorText: o[50],
    colorTextSecondary: o[300],
    colorTextTertiary: o[400],
    colorTextQuaternary: o[300],
    colorTextDescription: o[300],
    // =========================================================================
    // Dark Mode Border Overrides
    // =========================================================================
    colorBorder: o[600],
    colorBorderSecondary: o[700],
    colorSplit: o[700],
    // =========================================================================
    // Dark Mode Fill Overrides
    // =========================================================================
    colorFill: o[700],
    colorFillSecondary: o[800],
    colorFillTertiary: o[600],
    colorFillQuaternary: o[700],
    // =========================================================================
    // Dark Mode Primary Background
    // =========================================================================
    colorPrimaryBg: x.primary15,
    // =========================================================================
    // Dark Mode Semantic Backgrounds
    // =========================================================================
    // Ant Design status Tags use these base semantic tokens for their foreground
    // in filled and outlined variants. Keep the base and text aliases aligned,
    // as they are in the light theme, so semantic status text remains readable
    // on the dark semantic backgrounds below.
    colorSuccess: r.successTextDark,
    colorSuccessBg: r.successBgDark,
    colorSuccessBgHover: r.successBgDark,
    colorSuccessBorder: r.successBorderDark,
    colorSuccessBorderHover: r.successBorderDark,
    colorSuccessText: r.successTextDark,
    colorSuccessTextHover: r.successTextDark,
    colorSuccessTextActive: r.successTextDark,
    colorWarning: r.warningTextDark,
    colorWarningBg: r.warningBgDark,
    colorWarningBgHover: r.warningBgDark,
    colorWarningBorder: r.warningBorderDark,
    colorWarningBorderHover: r.warningBorderDark,
    colorWarningText: r.warningTextDark,
    colorWarningTextHover: r.warningTextDark,
    colorWarningTextActive: r.warningTextDark,
    colorError: r.errorTextDark,
    colorErrorBg: r.errorBgDark,
    colorErrorBgHover: r.errorBgDark,
    colorErrorBorder: r.errorBorderDark,
    colorErrorBorderHover: r.errorBorderDark,
    colorErrorText: r.errorTextDark,
    colorErrorTextHover: r.errorTextDark,
    colorErrorTextActive: r.errorTextDark,
    colorInfo: r.infoTextDark,
    colorInfoBg: r.infoBgDark,
    colorInfoBgHover: r.infoBgDark,
    colorInfoBorder: r.infoBorderDark,
    colorInfoBorderHover: r.infoBorderDark,
    colorInfoText: r.infoTextDark,
    colorInfoTextHover: r.infoTextDark,
    colorInfoTextActive: r.infoTextDark,
    // =========================================================================
    // Dark Mode Shadows
    // =========================================================================
    boxShadow: I.darkPrimary,
    boxShadowSecondary: I.darkSecondary
  },
  components: {
    // Inherit light component tokens and override where needed
    ...s.components,
    // =========================================================================
    // Button Dark Mode
    // =========================================================================
    Button: {
      ...s.components?.Button
    },
    // =========================================================================
    // Input Dark Mode
    // =========================================================================
    Input: {
      ...s.components?.Input,
      colorBgContainer: o[800],
      colorBorder: o[600]
    },
    // =========================================================================
    // Select Dark Mode
    // =========================================================================
    Select: {
      ...s.components?.Select,
      colorBorder: o[600]
    },
    // =========================================================================
    // Card Dark Mode
    // =========================================================================
    Card: {
      ...s.components?.Card,
      colorBgContainer: o[800]
    },
    // =========================================================================
    // Table Dark Mode
    // =========================================================================
    Table: {
      ...s.components?.Table,
      colorBgContainer: o[800],
      borderColor: o[700],
      headerBg: o[900],
      rowHoverBg: x.primary10
    },
    // Status Tags use semantic foregrounds as their solid background. The
    // default white solid text is not readable on the dark-mode foreground
    // palette, so scope the high-contrast text override to Tag only.
    Tag: {
      ...s.components?.Tag,
      // Filled status Tags use the component-level aliases below rather than
      // the semantic text aliases. Keep their foreground neutral and bright
      // against the translucent semantic fills on dark container surfaces.
      colorError: o[50],
      colorInfo: o[50],
      colorSuccess: o[50],
      colorTextLightSolid: o[900],
      colorWarning: o[50]
    },
    // =========================================================================
    // Menu Dark Mode
    // =========================================================================
    Menu: {
      ...s.components?.Menu,
      itemSelectedBg: x.primary15,
      itemSelectedColor: t.primary
    }
  }
}, w = {
  en_US: K,
  fr_FR: Y
}, O = W({
  locale: "en_US",
  setLocale: () => {
  }
}), A = "antd-locale", q = ({ children: e, initialLocale: d }) => {
  const [n, m] = C(d ?? "en_US");
  k(() => {
    if (d) return;
    let l = null;
    try {
      l = localStorage.getItem(A);
    } catch (p) {
      console.error("Error accessing localStorage:", p);
    }
    l && w[l] && m(l);
  }, [d]);
  const H = (l) => {
    m(l);
    try {
      localStorage.setItem(A, l);
    } catch (p) {
      console.error("Error writing localStorage:", p);
    }
  }, f = b(() => ({ locale: n, setLocale: H }), [n]);
  return /* @__PURE__ */ y(O.Provider, { value: f, children: /* @__PURE__ */ y(z, { locale: w[n], children: e }) });
}, sr = () => F(O), X = W({
  mode: "light",
  toggleTheme: () => {
  }
}), P = "theme-mode", R = "theme-mode-manual", J = (e) => ({
  "--app-theme-bg-layout": e.colorBgLayout,
  "--app-theme-bg-container": e.colorBgContainer,
  "--app-theme-bg-elevated": e.colorBgElevated,
  "--app-theme-border": e.colorBorder,
  "--app-theme-border-secondary": e.colorBorderSecondary,
  "--app-theme-split": e.colorSplit,
  "--app-theme-text": e.colorText,
  "--app-theme-text-secondary": e.colorTextSecondary,
  "--app-theme-text-tertiary": e.colorTextTertiary,
  "--app-theme-primary": e.colorPrimary,
  "--app-theme-primary-bg": e.colorPrimaryBg,
  "--app-theme-success": e.colorSuccess,
  "--app-theme-success-bg": e.colorSuccessBg,
  "--app-theme-success-border": e.colorSuccessBorder,
  "--app-theme-success-text": e.colorSuccessText,
  "--app-theme-warning": e.colorWarning,
  "--app-theme-warning-bg": e.colorWarningBg,
  "--app-theme-warning-border": e.colorWarningBorder,
  "--app-theme-warning-text": e.colorWarningText,
  "--app-theme-error": e.colorError,
  "--app-theme-error-bg": e.colorErrorBg,
  "--app-theme-error-border": e.colorErrorBorder,
  "--app-theme-error-text": e.colorErrorText,
  "--app-theme-fill-secondary": e.colorFillSecondary,
  "--app-theme-fill-tertiary": e.colorFillTertiary,
  "--app-theme-focus-ring": e.colorPrimaryBorder,
  "--app-theme-interactive-hover": e.colorFillTertiary
}), dr = ({ children: e, initialTheme: d }) => {
  const [n, m] = C(d ?? "light"), [H, f] = C(d !== void 0);
  k(() => {
    let i;
    try {
      if (d)
        m(d), f(!0);
      else {
        const L = localStorage.getItem(R) === "1";
        f(L);
        const Q = localStorage.getItem(P);
        if (L)
          m(Q === "dark" ? "dark" : "light");
        else {
          const u = window.matchMedia("(prefers-color-scheme: dark)"), T = () => m(u.matches ? "dark" : "light");
          T(), typeof u.addEventListener == "function" ? (u.addEventListener("change", T), i = () => u.removeEventListener("change", T)) : typeof u.addListener == "function" && (u.addListener(T), i = () => u.removeListener(T));
        }
      }
    } catch (h) {
      console.error("Error initializing theme mode:", h);
    }
    return i;
  }, [d]);
  const l = V(() => {
    f(!0), m((i) => i === "light" ? "dark" : "light");
  }, []);
  k(() => {
    try {
      localStorage.setItem(P, n), localStorage.setItem(R, H ? "1" : "0");
    } catch (i) {
      console.error("Error persisting theme mode:", i);
    }
  }, [n, H]);
  const p = b(() => n === "light" ? s : N, [n]), M = b(() => G.getDesignToken(p), [p]), E = b(() => J(M), [M]), U = b(() => ({ mode: n, toggleTheme: l }), [n, l]);
  return k(() => {
    if (typeof document > "u")
      return;
    const i = document.documentElement;
    return Object.entries(E).forEach(([h, L]) => {
      i.style.setProperty(h, L);
    }), () => {
      Object.keys(E).forEach((h) => {
        i.style.removeProperty(h);
      });
    };
  }, [E]), /* @__PURE__ */ y(q, { children: /* @__PURE__ */ y(X.Provider, { value: U, children: /* @__PURE__ */ y(z, { theme: p, children: /* @__PURE__ */ y($, { children: e }) }) }) });
}, gr = () => F(X), _ = {
  borderRadius: a.md,
  itemBg: "transparent",
  itemHoverBg: x.primary10,
  itemHoverColor: t.primary,
  itemSelectedBg: t.primaryLight,
  itemSelectedColor: t.primary,
  subMenuItemBg: "transparent",
  subMenuItemSelectedColor: t.primary
}, Z = {
  ..._,
  itemSelectedBg: x.primary15,
  darkGroupTitleColor: o[400],
  darkItemBg: "transparent",
  darkItemColor: o[300],
  darkItemDisabledColor: "rgba(249, 250, 251, 0.25)",
  darkItemHoverBg: x.primary10,
  darkItemHoverColor: o[50],
  darkItemSelectedBg: x.primary15,
  darkItemSelectedColor: t.primary,
  darkPopupBg: o[800],
  darkSubMenuItemBg: "transparent"
};
function mr(e) {
  return { ...e === "dark" ? Z : _ };
}
export {
  dr as AppThemeProvider,
  q as LocaleProvider,
  mr as createAdminSidebarMenuTokens,
  N as dark,
  s as light,
  sr as useLocale,
  gr as useTheme
};
//# sourceMappingURL=index.js.map
